import { PagedListRequestInterface, PagedResponseInterface, PaginatedAPIInterface } from '@vendasta/galaxy/table';
import {
  ActivationStatus,
  AppType,
  ListVendorAccountMetricsFilterOptions,
  ListVendorAccountMetricsRequest,
  ListVendorAccountMetricsSortOptions,
  PartnerEngagementApiService,
  VendorAccountMetrics,
} from '@vendasta/marketplace-analytics';
import { FilterGroup } from '@vendasta/va-filter2';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { FilterKeyForProducts, FilterKeyForResellers } from './accounts-table.component';

export class AccountsTableDataSource implements PaginatedAPIInterface<VendorAccountMetrics> {
  private activeAppIds$$: BehaviorSubject<Set<string>> = new BehaviorSubject(new Set());
  activeAppIds$: Observable<Set<string>> = this.activeAppIds$$
    .asObservable()
    .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)));

  constructor(
    private vendorId$: Observable<string>,
    private engagementService: PartnerEngagementApiService,
    private sortOptions$: Observable<ListVendorAccountMetricsSortOptions>,
    private filterGroup: FilterGroup,
  ) {}

  get(req: PagedListRequestInterface): Observable<PagedResponseInterface<VendorAccountMetrics>> {
    const { cursor, pageSize } = req.pagingOptions;
    const { text } = req.searchOptions;
    return combineLatest([
      this.vendorId$,
      this.sortOptions$,
      this.filterGroup.valueChanges.pipe(startWith(this.filterGroup.value)),
    ]).pipe(
      switchMap(([vendorId, sortOptions]) => {
        const request = new ListVendorAccountMetricsRequest({
          vendorId: vendorId,
          cursor: cursor,
          pageSize: pageSize,
          filterOptions: new ListVendorAccountMetricsFilterOptions({
            searchTerm: text,
          }),
          sortOptions: sortOptions,
        });
        this.updateFilterGroup(FilterKeyForResellers, request, 'resellerPartnerIds');
        this.updateFilterGroup(FilterKeyForProducts, request, 'appIds');
        if (request.filterOptions.appIds?.length) {
          request.filterOptions.activationStatuses = [
            ActivationStatus.ACTIVATION_STATUS_ACTIVE,
            ActivationStatus.ACTIVATION_STATUS_PENDING_DEACTIVATION,
          ];
        }
        return this.engagementService.listVendorAccountMetrics(request).pipe(
          map((res) => {
            return {
              data: res.vendorAccountMetrics ?? [],
              pagingMetadata: {
                nextCursor: res.nextCursor,
                hasMore: res.hasMore,
              },
            };
          }),
          tap((res) => {
            const appIds = res.data
              .map((metric) => metric.activations)
              .flat()
              .filter(
                (a) =>
                  a.activationStatus === ActivationStatus.ACTIVATION_STATUS_ACTIVE &&
                  a.appType === AppType.APP_TYPE_APP,
              )
              .map((a) => a.appId);
            this.activeAppIds$$.next(new Set(appIds));
          }),
        );
      }),
    );
  }

  updateFilterGroup(filterKey: string, request: ListVendorAccountMetricsRequest, keyName: string): void {
    let idArray = [];
    // need to check this.filterGroup.value here because the value from the valueChanges is null
    if (this.filterGroup?.value[filterKey]) {
      idArray = this.filterGroup.value[filterKey].map((value) => value.id);
    }
    if (idArray.length > 0) {
      request.filterOptions[keyName] = idArray;
    }
  }
}
